import React, { useState } from "react";
import SharedDataContext from "./sharedDataContext.jsx";
import { TryRounded } from "@mui/icons-material";
import roundToNearestMinutesWithOptions from "date-fns/fp/roundToNearestMinutesWithOptions";
import { additionalInformationNotification } from "../../config/notifications/pageNotifications.jsx";

import {
  formatCurrency,
  formatCurrencyNoDecimal,
} from "../../utilities/numericFormat.js";

//----------------------------------------------------------------

//* TODO: isDev and retrieveQuoteJourney PRE-DEPLOY
// TODO: MAKE 100% SURE THAT THIS IS SET TO FALSE (OR REMOVED COMPLETELY)
// const isDev = false;

const SharedDataState = (props) => {
  const initialState = {
    accessToken: null, //* We only get the Access Token from a create quote but we need it for all other calls to eLife so, store it here.
    isCoverStateValid: true, //* On the Quote Summary page, we need to tell other components in the stack that if the Total Premium is valid
    updatingQuoteSummary: false, //* This tells the createQuote not to be called on the layout page when we are editing on the Quote Summary page
    updateOktaUser: false,
    showErrorModal: false,
    isOutcomeDataLoaded: false, //* Have we run and complete the Outcome page
    hasOutcomeResponseRun: false,
    isRetrievePolicyExpired: false, //* for CC, we need to know if the Policy is expired on Outcome page.
    isPageLoading: false,
    pdsUrl: null,
    additionalConditionsChanged: false,
    retrievedCoverDetails: [],
    retrieveQuoteJourney: false, //! TODO: retrieveQuoteJourney IMPORTANT PRE-DEPLOY MAKE 100% SURE THAT THIS IS SET TO FALSE
    isDev: false, //! TODO: isDev IMPORTANT PRE-DEPLOY MAKE 100% SURE THAT THIS IS SET TO FALSE (OR REMOVED COMPLETELY) */
    errorMessage: null,
  };

  //* We get this access token from a call to createQuote or updateQuote
  //* Only used during dev/test when isDev is set to true.
  const devAccessToken =
    "eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOlsiaHR0cHM6Ly9kZXYuZWxpZmVpbnN1cmUuY29tLmF1L29hdXRoMi90b2tlbiJdLCJpc3MiOiJ0ZXJtbGlmZS1hcGkuZGV2Iiwic3ViIjoidGVybWxpZmUtYXBpLmRldi5qd3QiLCJpYXQiOjE3Mjg4NjE4NzgsImV4cCI6MTcyODg5MDY3OCwianRpIjoiZGQzYjExMjQtM2Q4Yi00NjM5LThjZWItY2QxOWRhMGZmMWUzIiwicWlkIjoiNjM1UUM1NjAzOEFMUCIsImVsdGlkIjoiODhmODViNDItMmM5ZS00NGMzLTg2ZTktOTRlZDIzZTM3ODMyIiwib2t0YVVzZXJJZCI6IjAwdTUwbzNtMXdQRnJLVGJqM2w3IiwiY2NfdXNlciI6ZmFsc2V9.f5cO5BKpSrk9NhOo1OM0za0eFz59LAf5_BEyh6CdG5c";

  const [state, setState] = useState(initialState);
  const handleEventChange = ({ name, value, from = "Not Set" }) => {
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleAdditionalConditionsChange = () => {
    setState((prevState) => ({
      ...prevState,
      ["additionalConditionsChanged"]: !prevState.additionalConditionsChanged,
    }));
  };

  const openErrorModal = ({ errorMessage = null }) => {
    handleEventChange({
      name: "showErrorModal",
      value: true,
    });

    if (errorMessage != null) {
      handleEventChange({
        name: "errorMessage",
        value: errorMessage,
      });
    }
  };

  const setPdsUrl = (url) => {
    if (url) {
      handleEventChange({
        name: "pdsUrl",
        value: url,
      });
    }
  };

  //* Cover details for a CC retrieval */
  const createCoverDisplay = (cover, insuredAmount, monthlyPremium) => {
    if (!insuredAmount && insuredAmount?.length <= 0) return;

    const insuredAmountLocal = formatCurrencyNoDecimal(insuredAmount);
    const monthlyPremiumLocal = formatCurrency(monthlyPremium);

    return `${cover} / ${insuredAmountLocal} / ${monthlyPremiumLocal} monthly`;
  };
  const setRetrievedCoverDetails = async (data) => {
    if (!data?.insuredParticipants[0]?.covers) return;

    const covers = data?.insuredParticipants[0]?.covers;
    const coverData = [];

    const lifeMonthly = covers?.life?.premiums["monthly"]
      ? covers?.life?.premiums["monthly"]
      : covers?.life["monthly"];

    const lifeCoverDisplay = covers?.life?.sumInsured
      ? createCoverDisplay("Life", covers?.life?.sumInsured, lifeMonthly)
      : null;

    coverData.push(lifeCoverDisplay);

    const ciMonthly = covers?.criticalIllness?.premiums["monthly"]
      ? covers?.criticalIllness?.premiums["monthly"]
      : covers?.criticalIllness["monthly"];

    const criticalIllnessCoverDisplay = covers?.criticalIllness?.sumInsured
      ? createCoverDisplay(
          "Critical Illness",
          covers?.criticalIllness?.sumInsured,
          ciMonthly
        )
      : null;
    coverData.push(criticalIllnessCoverDisplay);

    const uwMonthly = covers?.unableToWork?.premiums["monthly"]
      ? covers?.unableToWork?.premiums["monthly"]
      : covers?.unableToWork["monthly"];

    const unableToWorkCoverDisplay = covers?.unableToWork?.sumInsured
      ? createCoverDisplay(
          " Unable to work",
          covers?.unableToWork?.sumInsured,
          uwMonthly
        )
      : null;
    coverData.push(unableToWorkCoverDisplay);

    if (coverData) {
      handleEventChange({
        name: "retrievedCoverDetails",
        value: coverData,
      });
    }
  };

  return (
    <SharedDataContext.Provider
      value={{
        state,
        accessToken: state.isDev === false ? state.accessToken : devAccessToken,
        isCoverStateValid: state.isCoverStateValid,
        updatingQuoteSummary: state.updatingQuoteSummary,
        handleEventChange: handleEventChange,
        handleAdditionalConditionsChange: handleAdditionalConditionsChange,
        updateOktaUser: state.updateOktaUser,
        showErrorModal: state.showErrorModal,
        openErrorModal,
        isOutcomeDataLoaded: state.isOutcomeDataLoaded,
        errorMessage: state.errorMessage,
        retrieveQuoteJourney: state.retrieveQuoteJourney,
        isRetrievePolicyExpired: state.isRetrievePolicyExpired,
        isPageLoading: state.isPageLoading,
        isDevShared: state.isDev,
        isApplied: true,
        pdsUrl: state.pdsUrl,
        additionalConditionsChanged: state.additionalConditionsChanged,
        setPdsUrl,
        retrievedCoverDetails: state.retrievedCoverDetails,
        setRetrievedCoverDetails,
      }}
    >
      {props.children}
    </SharedDataContext.Provider>
  );
};

export default SharedDataState;
